import { EstimateFormData } from '@/types/estimates/EstimateFormData';
import { addDays } from 'date-fns';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { Control } from 'react-hook-form';
import EstimateDayCard from './EstimateDayCard';
import { CircuitDayFormData } from '@/types/circuits/CircuitDaysFormData';
import { OptionalBenefitComputationFn } from '../EstimateDetailsAndOptions';
import { SelectedEstimateOption } from '../../../types/estimates/SelectedEstimateOptionType';
import { SupportedLocale } from '@/types/supported-locale.type';

interface EstimateDaysCardsProps {
  control: Control<EstimateFormData>;
  arrivalDate: Date;
  estimateDays: CircuitDayFormData[];
  computeOptionalBenefitPrice: OptionalBenefitComputationFn;
  selectedOptions: Record<string, SelectedEstimateOption>;
  setSelectedOptions: Dispatch<
    SetStateAction<Record<string, SelectedEstimateOption>>
  >;
  adultParticipants: EstimateFormData['adults'];
  childParticipants: EstimateFormData['children'];
  currency?: string;
  creationDate?: Date;
  accessToken?: string;
  isViewMode?: boolean;
  language?: SupportedLocale;
  showTranslation?: boolean;
  isEditableCircuit?: boolean;
}

const EstimateDaysCards = ({
  control,
  arrivalDate,
  estimateDays,
  computeOptionalBenefitPrice,
  selectedOptions,
  setSelectedOptions,
  adultParticipants,
  childParticipants,
  currency = 'EUR',
  creationDate,
  accessToken,
  isViewMode = false,
  language,
  showTranslation = true,
  isEditableCircuit = false,
}: EstimateDaysCardsProps) => {
  const daysData = useMemo(
    () =>
      estimateDays.map((circuitDay, index) => ({
        circuitDay,
        date: addDays(arrivalDate, index),
      })),
    [arrivalDate, estimateDays],
  );

  return daysData.map(({ circuitDay, date }, index) => (
    <EstimateDayCard
      control={control}
      dayIndex={index}
      date={date}
      circuitDay={circuitDay}
      computeOptionalBenefitPrice={computeOptionalBenefitPrice}
      selectedOptions={selectedOptions}
      setSelectedOptions={setSelectedOptions}
      key={`estimate-day-${date.toISOString()}`}
      adultParticipants={adultParticipants}
      childParticipants={childParticipants}
      currency={currency}
      creationDate={creationDate}
      language={language}
      accessToken={accessToken}
      isViewMode={isViewMode}
      showTranslation={showTranslation}
      isEditableCircuit={isEditableCircuit}
    />
  ));
};

export default EstimateDaysCards;
